import * as React from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/sonner';
import { AuthLayout } from '@/components/layout/auth-layout';
import { useLogin } from '@/lib/auth';

const LoginSchema = z.object({
  email: z.string().email('Wprowadź poprawny adres e-mail'),
  password: z.string().min(1, 'Hasło jest wymagane'),
});
type LoginInput = z.infer<typeof LoginSchema>;

export const Route = createFileRoute('/login')({
  staticData: { public: true },
  validateSearch: (s): { redirect?: string } => ({ redirect: typeof s.redirect === 'string' ? s.redirect : undefined }),
  component: LoginPage,
});

function LoginPage() {
  const search = Route.useSearch();
  const navigate = useNavigate();
  const login = useLogin();

  const form = useForm<LoginInput>({
    resolver: zodResolver(LoginSchema),
    defaultValues: { email: '', password: '' },
  });

  const onSubmit = async (data: LoginInput) => {
    try {
      await login.mutateAsync(data);
      navigate({ to: search.redirect ?? '/' });
    } catch (e) {
      const message =
        (e as Error).message === 'invalid_credentials'
          ? 'Niepoprawny adres e-mail lub hasło'
          : 'Nie udało się zalogować. Spróbuj ponownie.';
      // Field-level error keeps the form's a11y semantics; toast surfaces
      // the failure prominently (slow networks, fat-finger emails).
      form.setError('password', { message });
      toast.error(message);
    }
  };

  return (
    <AuthLayout>
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Zaloguj się</CardTitle>
          <CardDescription>
            Panel klienta pozyskajpacjenta.pl. Jeśli jesteś operatorem, użyj{' '}
            <Link to="/operator/login" className="text-accent underline-offset-4 hover:underline">
              logowania operatora
            </Link>
            .
          </CardDescription>
        </CardHeader>
        <form onSubmit={form.handleSubmit(onSubmit)} noValidate>
          <CardContent className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">E-mail</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                {...form.register('email')}
                aria-invalid={!!form.formState.errors.email}
              />
              {form.formState.errors.email ? (
                <p className="text-xs text-error">{form.formState.errors.email.message}</p>
              ) : null}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Hasło</Label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                {...form.register('password')}
                aria-invalid={!!form.formState.errors.password}
              />
              {form.formState.errors.password ? (
                <p className="text-xs text-error">{form.formState.errors.password.message}</p>
              ) : null}
            </div>
            <Button type="submit" className="w-full" disabled={login.isPending}>
              {login.isPending ? 'Logowanie…' : 'Zaloguj się'}
            </Button>
          </CardContent>
        </form>
      </Card>
    </AuthLayout>
  );
}
